import type { GetCampaignResponse } from '@ab-inbev-eu/campaign/src/models/campaign';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist, type StateStorage } from 'zustand/middleware';
import type { ICampaignActionResponse } from '@ab-inbev-eu/engagement/src/utils/types';
import { SupportedConsent } from '@ab-inbev-eu/user/src/models';

// In-memory fallback used when localStorage writes fail (most commonly:
// the per-origin quota already consumed by third-party SDKs such as Braze,
// Segment, or Datadog, or storage blocked/limited by the browser, e.g.
// Safari private mode or in-app webviews). Keeps the store fully functional
// for the current session even though state won't survive a reload in
// that case, instead of throwing and breaking code that relies on this data.
const memoryFallback = new Map<string, string>();
let useMemoryFallback = false;

const resilientStorage: StateStorage = {
  getItem: (name) => {
    if (useMemoryFallback) return memoryFallback.get(name) ?? null;
    try {
      return localStorage.getItem(name);
    } catch {
      return memoryFallback.get(name) ?? null;
    }
  },
  setItem: (name, value) => {
    if (useMemoryFallback) {
      memoryFallback.set(name, value);
      return;
    }
    try {
      localStorage.setItem(name, value);
    } catch (error) {
      // Retry once after clearing our own prior entry, in case it was a
      // stale/oversized value. If it still fails, the quota is most likely
      // consumed by other keys on this origin (third-party SDKs) rather than
      // by this store, so fall back to memory for the rest of the session.
      try {
        localStorage.removeItem(name);
        localStorage.setItem(name, value);
      } catch {
        useMemoryFallback = true;
        memoryFallback.set(name, value);
        // eslint-disable-next-line no-console
        console.warn(
          '[dtc-application-storage] localStorage write failed (likely quota exceeded), falling back to in-memory storage for this session',
          error,
        );
      }
    }
  },
  removeItem: (name) => {
    if (useMemoryFallback) {
      memoryFallback.delete(name);
      return;
    }
    try {
      localStorage.removeItem(name);
    } catch {
      memoryFallback.delete(name);
    }
  },
};

interface IState {
  campaign?: GetCampaignResponse;
  campaignActions?: ICampaignActionResponse['data'];
  lastSmsSentAt?: number;
  lastEmailSentAt?: number;
  cardScanAnimationComplete: boolean;
  triviaCompleted?: boolean;
  consents?: Record<SupportedConsent, boolean>;
  lastPitchText?: string;
  lastWonCardId?: number;
  lastSelectedPocData?: { name: string; address?: string };
  selectStoreMode: boolean | null;
  selectedRetailerId: number | null;
}

export const useApplicationStore = create<IState>()(
  devtools(
    persist(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      (_set) => ({
        campaign: undefined,
        lastSmsSentAt: undefined,
        lastEmailSentAt: undefined,
        campaignActions: undefined,
        cardScanAnimationComplete: false,
        triviaCompleted: false,
        consents: undefined,
        lastPitchText: '',
        lastWonCardId: undefined,
        lastSelectedPocData: undefined,
        selectStoreMode: null,
        selectedRetailerId: null,
      }),
      {
        name: 'dtc-application-storage',
        storage: createJSONStorage(() => resilientStorage),
        partialize(state) {
          const {
            lastSmsSentAt,
            lastEmailSentAt,
            cardScanAnimationComplete,
            triviaCompleted,
            lastPitchText,
            consents,
            lastWonCardId,
            lastSelectedPocData,
            selectStoreMode,
            selectedRetailerId,
          } = state;
          return {
            lastSmsSentAt,
            lastEmailSentAt,
            cardScanAnimationComplete,
            triviaCompleted,
            lastPitchText,
            consents,
            lastWonCardId,
            lastSelectedPocData,
            selectStoreMode,
            selectedRetailerId,
          };
        },
      },
    ),
  ),
);
